import { useTranslation } from 'react-i18next';
import type { UseTransactionsResult } from '@/api';
import type { TransactionsCategory } from '@/domain/transactions';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import { EmptyState } from '../common/empty-state';
import { Icon } from '../common/icon';
import { Spinner } from '../common/spinner';
import { Toast } from '../common/toast';
import { TransactionLedgerRow } from './TransactionLedgerRow';

export interface TransactionsPanelProps {
  transactions: UseTransactionsResult;
  className?: string;
}

const CATEGORY_CHIPS: {
  value: TransactionsCategory;
  labelKey: string;
  defaultLabel: string;
}[] = [
  { value: 'all', labelKey: 'All', defaultLabel: 'All' },
  { value: 'sports', labelKey: 'Sports', defaultLabel: 'Sports' },
  { value: 'casino', labelKey: 'Casino', defaultLabel: 'Casino' },
];

function FilterChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'rounded-md px-3 py-2 text-[13px] whitespace-nowrap transition-colors',
        selected
          ? 'bg-action-primary font-bold text-text-on-action'
          : 'bg-bg-elevated font-normal text-text-secondary hover:text-text-primary',
      )}
    >
      {label}
    </button>
  );
}

/**
 * Profile transactions body: category chips, My Bets–style date preset, infinite ledger.
 */
export function TransactionsPanel({ transactions, className }: TransactionsPanelProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const {
    category,
    setCategory,
    dateDays,
    setDateDays,
    datePresets,
    rows,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    listError,
    currency,
    sentinelRef,
  } = transactions;

  return (
    <div className={cn('flex flex-col gap-3 lg:gap-3', className)}>
      <div
        role="group"
        aria-label={label('Transaction type', 'Transaction type')}
        className="flex flex-wrap gap-2"
      >
        {CATEGORY_CHIPS.map((chip) => (
          <FilterChip
            key={chip.value}
            label={label(chip.labelKey, chip.defaultLabel)}
            selected={category === chip.value}
            onClick={() => setCategory(chip.value)}
          />
        ))}
      </div>

      <label className="relative block w-full shrink-0 sm:max-w-[280px]">
        <span className="sr-only">{label('DateFilter', 'Date filter')}</span>
        <select
          value={dateDays}
          onChange={(event) =>
            setDateDays(Number(event.target.value) as typeof dateDays)
          }
          className={cn(
            'h-9 w-full appearance-none rounded-md border border-border-subtle bg-bg-elevated',
            'px-3 py-2 pr-8 text-xs text-text-primary outline-none',
            'focus-visible:ring-1 focus-visible:ring-border-focus',
          )}
        >
          {datePresets.map((preset) => (
            <option key={preset.days} value={preset.days}>
              {label(preset.labelKey, preset.defaultLabel)}
            </option>
          ))}
        </select>
        <Icon
          name="chevron-down"
          size={12}
          className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-icon-muted"
        />
      </label>

      {listError ? (
        <Toast tone="danger">
          {label('Could not load transactions', 'Could not load transactions')}
          {listError.message ? `: ${listError.message}` : ''}
        </Toast>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner label={label('Loading transactions', 'Loading transactions')} />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon="transactions"
          title={label('No transactions', 'No transactions')}
          description={label(
            'No wallet activity matches these filters.',
            'No wallet activity matches these filters.',
          )}
        />
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((row) => (
            <TransactionLedgerRow
              key={`${row.Id}-${row.Type}-${row.Date}`}
              row={row}
              currency={currency}
              cashLabel={label('Cash', 'Cash')}
              bonusLabel={label('Bonus', 'Bonus')}
            />
          ))}

          <div
            ref={sentinelRef}
            className="flex items-center justify-center py-2 lg:py-3"
          >
            {isFetchingNextPage ? (
              <Spinner size="sm" label={null} />
            ) : hasNextPage ? (
              <p className="text-[12px] text-text-secondary">
                {label('Scroll for more…', 'Scroll for more…')}
              </p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
